// FASE 4.1 — Mentor IA chat page (/shadow/mentor). Sessions sidebar + chat.
import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Send, Plus, Loader2, Sparkles, Trash2, AlertCircle, MessageSquare, Crown } from "lucide-react";
import { Navbar } from "../components/Navbar";
import { API, safeJson } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { useLanguage } from "../i18n/LanguageContext";
import { tFases } from "../i18n/shadow-fases";
import { startProCheckout } from "../components/shadow/ProGate";

export default function ShadowMentorPage() {
  const { user, token, openLogin, isPro } = useAuth();
  const { lang } = useLanguage();
  const [sessions, setSessions] = useState([]);
  const [active, setActive] = useState(null); // {id, title}
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [quota, setQuota] = useState({ used_today: 0, daily_limit: 10, unlimited: false });
  const scrollerRef = useRef(null);

  const headers = token ? { Authorization: `Bearer ${token}` } : {};

  const loadSessions = useCallback(async () => {
    const r = await fetch(`${API}/shadow/mentor/sessions`, { credentials: "include", headers });
    const d = await safeJson(r);
    if (r.ok && d) {
      setSessions(d.sessions || []);
      if (d.quota) setQuota(d.quota);
    }
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadSession = useCallback(async (id) => {
    const r = await fetch(`${API}/shadow/mentor/sessions/${id}`, { credentials: "include", headers });
    const d = await safeJson(r);
    if (r.ok && d) {
      setActive({ id, title: d.session?.title });
      setMessages(d.messages || []);
    }
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    let alive = true;
    const init = async () => {
      if (!user) return;
      await loadSessions();
      if (!alive) return;
    };
    init();
    return () => { alive = false; };
  }, [user, loadSessions]);

  useEffect(() => {
    if (scrollerRef.current) scrollerRef.current.scrollTop = scrollerRef.current.scrollHeight;
  }, [messages]);

  const newSession = useCallback(async () => {
    const r = await fetch(`${API}/shadow/mentor/sessions`, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json", ...headers },
    });
    const d = await safeJson(r);
    if (r.ok && d?.session) {
      setSessions((s) => [d.session, ...s]);
      setActive({ id: d.session.id, title: d.session.title });
      setMessages([]);
      setErr(null);
    }
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  const deleteSession = async (id) => {
    if (!window.confirm(tFases(lang, "m_confirm_delete"))) return;
    await fetch(`${API}/shadow/mentor/sessions/${id}`, { method: "DELETE", credentials: "include", headers });
    setSessions((s) => s.filter((x) => x.id !== id));
    if (active?.id === id) { setActive(null); setMessages([]); }
  };

  const send = useCallback(async (e) => {
    e?.preventDefault?.();
    if (busy || !draft.trim()) return;
    let sessId = active?.id;
    if (!sessId) {
      // create on the fly
      const r = await fetch(`${API}/shadow/mentor/sessions`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json", ...headers },
      });
      const d = await safeJson(r);
      if (!r.ok || !d?.session?.id) { setErr(tFases(lang, "m_err_session")); return; }
      sessId = d.session.id;
      setActive({ id: sessId, title: d.session.title });
      setSessions((s) => [d.session, ...s]);
    }
    const text = draft.trim().slice(0, 2000);
    setDraft("");
    setBusy(true);
    setErr(null);
    setMessages((m) => [...m, { role: "user", content: text, ts: new Date().toISOString() }]);
    try {
      const r = await fetch(`${API}/shadow/mentor/sessions/${sessId}/message`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json", ...headers },
        body: JSON.stringify({ content: text }),
      });
      const d = await safeJson(r);
      if (!r.ok) {
        const msg = d?.detail?.message || d?.detail || tFases(lang, "m_err_default");
        throw new Error(typeof msg === "string" ? msg : tFases(lang, "m_err_default"));
      }
      setMessages((m) => [...m, d.assistant_message]);
      if (d.quota) setQuota(d.quota);
      // Title may have been auto-set; refresh list lightly.
      loadSessions();
    } catch (e2) {
      setErr(e2.message);
    } finally { setBusy(false); }
  }, [busy, draft, active, loadSessions, token]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!user) {
    return (
      <div className="min-h-screen bg-black text-white">
        <Navbar />
        <section className="mx-auto max-w-2xl px-6 pt-40 pb-24 text-center">
          <Sparkles className="mx-auto h-10 w-10 text-violet-300" />
          <h1 className="mt-4 text-3xl font-black tracking-tight">{tFases(lang, "m_title")}</h1>
          <p className="mt-2 text-sm text-white/55">{tFases(lang, "m_login_sub")}</p>
          <button onClick={openLogin} className="mt-6 inline-flex items-center gap-2 rounded-full bg-emerald-400 px-6 py-3 text-xs font-black uppercase tracking-[0.22em] text-black" data-testid="mentor-login-btn">
            {tFases(lang, "common_login")}
          </button>
        </section>
      </div>
    );
  }

  // Mentor IA is PRO-only. Show an upgrade gate to FREE users so they
  // can go straight to checkout instead of consuming tokens / quota.
  if (!isPro) {
    return (
      <div className="min-h-screen bg-black text-white" data-testid="mentor-pro-gate">
        <Navbar />
        <section className="mx-auto max-w-2xl px-6 pt-40 pb-24 text-center">
          <div className="inline-flex h-14 w-14 items-center justify-center rounded-full border border-amber-400/40 bg-amber-400/10">
            <Crown className="h-7 w-7 text-amber-300" />
          </div>
          <h1 className="mt-5 text-3xl font-black tracking-tight">{tFases(lang, "m_title")}</h1>
          <p className="mt-2 text-sm text-white/55">
            {{
              es: "Mentor IA es una funcionalidad exclusiva de RE₿EL PRO. Hazte PRO para chatear sin límites con tu mentor personal powered by Claude.",
              en: "Mentor IA is a RE₿EL PRO-only feature. Upgrade to chat without limits with your personal Claude-powered mentor.",
              sk: "Mentor IA je funkcia výlučne pre RE₿EL PRO. Aktualizuj na PRO a chatuj bez obmedzení s mentorom poháňaným Claude.", de: "IA-Mentor ist eine exklusive Funktion von RE₿EL PRO. Werde PRO, um unbegrenzt mit deinem persönlichen Mentor powered by Claude zu chatten.", "zh-CN": "Mentor IA 是 RE₿EL PRO 的专属功能。升级至 PRO 即可与您的个人导师无限畅聊，由 Claude 提供支持。",
            }[lang] || ""}
          </p>
          <button
            onClick={() => startProCheckout().catch(() => {})}
            data-testid="mentor-pro-cta"
            className="mt-6 inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-orange-500 to-amber-600 px-6 py-3 text-xs font-black uppercase tracking-[0.22em] text-black shadow-[0_8px_24px_rgba(255,140,0,0.35)] transition-all hover:-translate-y-0.5 hover:shadow-[0_12px_36px_rgba(255,140,0,0.55)]"
          >
            <Crown className="h-4 w-4" />
            {tFases(lang, "j_pro_cta")}
          </button>
          <p className="mt-3 text-[10px] font-mono uppercase tracking-[0.22em] text-white/40">€39.90 · lifetime</p>
        </section>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white" data-testid="mentor-page">
      <Navbar />
      <section className="mx-auto max-w-6xl px-4 pt-28 pb-12">
        <Link to="/shadow" className="inline-flex items-center gap-2 text-xs font-mono uppercase tracking-[0.22em] text-white/40 hover:text-violet-300" data-testid="mentor-back">
          <ArrowLeft className="h-3.5 w-3.5" /> {tFases(lang, "common_back")}
        </Link>
        <div className="mt-6 grid grid-cols-1 gap-4 md:grid-cols-[260px_1fr]">
          {/* Sessions sidebar */}
          <aside className="rounded-2xl border border-white/10 bg-white/[0.025] p-3 backdrop-blur md:max-h-[68vh] md:overflow-y-auto">
            <button onClick={newSession} className="flex w-full items-center justify-center gap-1.5 rounded-xl bg-gradient-to-r from-violet-500 to-fuchsia-500 px-4 py-2.5 text-[11px] font-black uppercase tracking-[0.16em] text-white hover:-translate-y-0.5 transition-all" data-testid="mentor-new-session">
              <Plus className="h-3.5 w-3.5" /> {tFases(lang, "m_new")}
            </button>
            <div className="mt-3 space-y-1">
              {sessions.length === 0 ? (
                <p className="px-2 py-3 text-[11px] text-white/40">{tFases(lang, "m_empty")}</p>
              ) : sessions.map((s) => (
                <div
                  key={s.id}
                  className={`group flex items-center justify-between gap-1 rounded-lg px-2.5 py-2 transition-colors ${active?.id === s.id ? "bg-violet-400/10 text-violet-200" : "hover:bg-white/[0.04] text-white/75"}`}
                  data-testid={`mentor-session-${s.id}`}
                >
                  <button onClick={() => loadSession(s.id)} className="flex flex-1 min-w-0 items-center gap-2 text-left">
                    <MessageSquare className="h-3.5 w-3.5 shrink-0" />
                    <span className="truncate text-xs">{s.title}</span>
                  </button>
                  <button onClick={() => deleteSession(s.id)} className="invisible rounded p-1 text-white/40 hover:text-rose-300 group-hover:visible" aria-label="Borrar">
                    <Trash2 className="h-3 w-3" />
                  </button>
                </div>
              ))}
            </div>
          </aside>

          {/* Chat */}
          <div className="flex h-[68vh] flex-col rounded-2xl border border-white/10 bg-white/[0.025] backdrop-blur">
            <div className="border-b border-white/10 px-4 py-2.5 text-[10px] font-mono uppercase tracking-[0.22em] text-violet-200/85">
              <Sparkles className="mr-1 inline h-3 w-3" /> {tFases(lang, "m_title")} · Claude
              <span className="ml-3 text-white/45" data-testid="mentor-quota">
                {quota.unlimited ? tFases(lang, "m_quota_pro") : tFases(lang, "m_quota_used", { u: quota.used_today, l: quota.daily_limit })}
              </span>
            </div>
            <div ref={scrollerRef} className="flex-1 overflow-y-auto px-4 py-4 space-y-3" data-testid="mentor-scroller">
              {messages.length === 0 ? (
                <div className="text-center py-12 text-white/45">
                  <Sparkles className="mx-auto h-8 w-8 text-violet-300/70" />
                  <p className="mt-3 text-sm">{tFases(lang, "m_examples")}</p>
                  <ul className="mt-2 space-y-1 text-xs">
                    <li className="cursor-pointer hover:text-violet-300" onClick={() => setDraft(tFases(lang, "m_ex1"))}>· {tFases(lang, "m_ex1")}</li>
                    <li className="cursor-pointer hover:text-violet-300" onClick={() => setDraft(tFases(lang, "m_ex2"))}>· {tFases(lang, "m_ex2")}</li>
                    <li className="cursor-pointer hover:text-violet-300" onClick={() => setDraft(tFases(lang, "m_ex3"))}>· {tFases(lang, "m_ex3")}</li>
                  </ul>
                </div>
              ) : messages.map((m, i) => (
                <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`} data-testid={`mentor-msg-${m.role}-${i}`}>
                  <div className={`max-w-[80%] rounded-2xl px-3.5 py-2.5 text-sm leading-relaxed ${
                    m.role === "user"
                      ? "bg-violet-500/[0.18] border border-violet-400/30 text-violet-50"
                      : "bg-white/[0.04] border border-white/10 text-white/85"
                  }`}>
                    {m.role === "assistant" && <div className="mb-1 text-[9px] font-mono uppercase tracking-[0.22em] text-violet-300/80">{tFases(lang, "m_role")}</div>}
                    <span className="whitespace-pre-wrap">{m.content}</span>
                  </div>
                </div>
              ))}
              {busy && (
                <div className="flex justify-start">
                  <div className="rounded-2xl border border-white/10 bg-white/[0.04] px-3.5 py-2.5 text-xs text-white/55">
                    <Loader2 className="inline h-3 w-3 animate-spin mr-1" /> {tFases(lang, "m_thinking")}
                  </div>
                </div>
              )}
              {err && (
                <div className="rounded-xl border border-rose-500/40 bg-rose-500/[0.08] px-3 py-2 text-xs text-rose-200" data-testid="mentor-error">
                  <AlertCircle className="inline h-3.5 w-3.5 mr-1" /> {err}
                </div>
              )}
            </div>
            <form onSubmit={send} className="border-t border-white/10 p-3">
              <div className="flex gap-2">
                <textarea
                  value={draft}
                  onChange={(e) => setDraft(e.target.value.slice(0, 2000))}
                  onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { send(e); } }}
                  rows={1}
                  placeholder={tFases(lang, "m_input_ph")}
                  className="flex-1 resize-none rounded-xl border border-white/10 bg-black/40 px-3 py-2.5 text-sm outline-none focus:border-violet-400/50"
                  data-testid="mentor-input"
                  disabled={busy || (quota.daily_limit !== null && !quota.unlimited && quota.used_today >= quota.daily_limit)}
                />
                <button type="submit" disabled={busy || !draft.trim()} className="inline-flex items-center gap-1 rounded-xl bg-gradient-to-r from-violet-500 to-fuchsia-500 px-4 py-2.5 text-[11px] font-black uppercase tracking-wider text-white disabled:opacity-50" data-testid="mentor-send">
                  {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}
                </button>
              </div>
            </form>
          </div>
        </div>
      </section>
    </div>
  );
}
